import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';
import {
  MaintenanceWindow,
  MaintenanceStatusListener,
  MaintenanceStatus,
  MaintenanceWindowInput,
} from './Maintenance.types';
import { getMaintenanceStatus, sortMaintenanceWindows } from './MaintenanceWindowResolver';
import { ServerDateService } from './ServerDate/ServerDate.service';

export class MaintenanceWindowService {
  private static readonly MAX_TIMER_DELAY = 2_147_483_647;
  private static readonly TIMER_BOUNDARY_BUFFER_MS = 50;

  private windows: MaintenanceWindow[] = [];
  private listeners = new Set<MaintenanceStatusListener>();

  private initialized = false;
  private initializingPromise: Promise<void> | null = null;

  private status: MaintenanceStatus = {
    phase: 'initializing',
    window: null,
  };

  private timerId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly serverDateService: ServerDateService,
    windows: MaintenanceWindowInput[] = []
  ) {
    this.windows = this.normalizeWindows(windows);
  }

  async initialize(): Promise<this> {
    if (this.initialized) {
      return this;
    }

    if (!this.initializingPromise) {
      this.initializingPromise = this.initializeInternal().finally(() => {
        this.initializingPromise = null;
      });
    }

    await this.initializingPromise;
    return this;
  }

  private async initializeInternal(): Promise<void> {
    await this.serverDateService.syncServerDate();

    this.initialized = true;
    this.evaluateAndNotify();
    this.scheduleNextEvaluation();
  }

  getCurrentStatus(): MaintenanceStatus {
    return this.status;
  }

  getCurrentWindow(): MaintenanceWindow | null {
    return this.status.window;
  }

  isOngoing(): boolean {
    return this.status.phase === 'ongoing';
  }

  refresh(windows: MaintenanceWindowInput[]): void {
    this.windows = this.normalizeWindows(windows);

    if (!this.initialized) {
      return;
    }

    this.evaluateAndNotify();
    this.scheduleNextEvaluation();
  }

  subscribe(listener: MaintenanceStatusListener): () => void {
    this.listeners.add(listener);
    this.safeNotify(listener, this.status);

    return () => {
      this.listeners.delete(listener);
    };
  }

  unsubscribe(listener: MaintenanceStatusListener): void {
    this.listeners.delete(listener);
  }

  destroy(): void {
    this.clearTimer();
    this.listeners.clear();
  }

  private evaluateAndNotify(): void {
    const nextStatus = getMaintenanceStatus(this.serverDateService.date(), this.windows);

    if (this.areStatusesEqual(this.status, nextStatus)) {
      return;
    }

    this.status = nextStatus;

    for (const listener of Array.from(this.listeners)) {
      this.safeNotify(listener, this.status);
    }
  }

  private scheduleNextEvaluation(): void {
    this.clearTimer();

    const now = this.serverDateService.date().getTime();

    const nextBoundary = this.windows
      .flatMap((window) => [window.startDate.getTime(), window.endDate.getTime()])
      .filter((time) => time > now)
      .sort((a, b) => a - b)[0];

    if (nextBoundary === undefined) {
      return;
    }

    const delay = Math.max(0, nextBoundary - now);
    const safeDelay = Math.min(
      delay + MaintenanceWindowService.TIMER_BOUNDARY_BUFFER_MS,
      MaintenanceWindowService.MAX_TIMER_DELAY
    );

    this.timerId = setTimeout(() => {
      this.evaluateAndNotify();
      this.scheduleNextEvaluation();
    }, safeDelay);
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private normalizeWindows(inputs: MaintenanceWindowInput[]): MaintenanceWindow[] {
    const windows: MaintenanceWindow[] = [];

    for (const input of inputs) {
      const window: MaintenanceWindow = {
        id: input.id,
        startDate: new Date(input.startDate),
        endDate: new Date(input.endDate),
      };

      const startTime = window.startDate.getTime();
      const endTime = window.endDate.getTime();

      const isValid =
        window.id.trim().length > 0 && Number.isFinite(startTime) && Number.isFinite(endTime) && startTime < endTime;

      if (!isValid) {
        logger.warn('[MAINTENANCE_WINDOW_SERVICE] Invalid maintenance window ignored:', input);
        continue;
      }

      windows.push(window);
    }

    const sortedWindows = sortMaintenanceWindows(windows);
    this.warnAboutOverlaps(sortedWindows);

    return sortedWindows;
  }

  private warnAboutOverlaps(windows: MaintenanceWindow[]): void {
    for (let index = 1; index < windows.length; index += 1) {
      const previous = windows[index - 1];
      const current = windows[index];

      if (current.startDate.getTime() < previous.endDate.getTime()) {
        logger.warn('[MAINTENANCE_WINDOW_SERVICE] Overlapping maintenance windows detected:', {
          previous,
          current,
        });
      }
    }
  }

  private safeNotify(listener: MaintenanceStatusListener, status: MaintenanceStatus): void {
    try {
      listener(status);
    } catch (error) {
      logger.error('[MAINTENANCE_WINDOW_SERVICE] Maintenance window listener failed:', {
        error: parseErrorMessage(error),
      });
    }
  }

  private areStatusesEqual(previous: MaintenanceStatus, next: MaintenanceStatus): boolean {
    return (
      previous.phase === next.phase &&
      previous.window?.id === next.window?.id &&
      previous.window?.startDate.getTime() === next.window?.startDate.getTime() &&
      previous.window?.endDate.getTime() === next.window?.endDate.getTime()
    );
  }
}
